import React from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '../contexts/WishlistContext';

const WishlistButton = ({ 
  item, 
  itemType, 
  className = "",
  size = "default", // "small", "default", "large"
  showLabel = false,
  variant = "default" // "default", "filled", "outline"
}) => {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  
  const isWishlisted = isInWishlist(item.id, itemType);
  
  const handleToggleWishlist = (e) => {
    e.stopPropagation(); // Prevent parent click events
    
    if (isWishlisted) {
      removeFromWishlist({ id: item.id, type: itemType });
    } else {
      addToWishlist({
        id: item.id,
        type: itemType,
        name: item.name || item.title,
        description: item.description || item.excerpt,
        image: item.image,
        slug: item.slug,
        price: item.price || item.regular_license_price,
        addedAt: new Date().toISOString()
      });
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-6 h-6';
      case 'large':
        return 'w-8 h-8';
      default:
        return 'w-7 h-7';
    }
  };

  const getVariantClasses = () => {
    const baseClasses = "flex items-center justify-center rounded-full transition-all duration-200 hover:scale-110";
    const sizeClasses = getSizeClasses();
    
    switch (variant) {
      case 'filled':
        return `${baseClasses} ${sizeClasses} ${
          isWishlisted 
            ? 'bg-red-500 hover:bg-red-600 text-white' 
            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
        }`;
      
      case 'outline':
        return `${baseClasses} ${sizeClasses} border-2 ${
          isWishlisted 
            ? 'border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
            : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
        }`;
      
      default:
        return `${baseClasses} ${sizeClasses} ${
          isWishlisted 
            ? 'bg-red-500/10 hover:bg-red-500/20 text-red-500 dark:text-red-400' 
            : 'bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300'
        }`;
    }
  };

  const getHeartIconClasses = () => {
    const baseClasses = "transition-all duration-200";
    
    if (isWishlisted) {
      return `${baseClasses} fill-current`;
    }
    
    return baseClasses;
  };

  return (
    <button
      onClick={handleToggleWishlist}
      className={getVariantClasses()}
      title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
    >
      <Heart className={`w-4 h-4 flex-shrink-0 ${getHeartIconClasses()}`} style={{ display: 'block', visibility: 'visible' }} />
      {showLabel && (
        <span className="ml-1 text-xs">
          {isWishlisted ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
};

export default WishlistButton; 